import Button from '@rio-cloud/rio-uikit/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import type { RootState } from '../../../../configuration/setup/store';
import { SUBSCRIPTION_WRITE } from '../../common/permissions/permissions';
import { getPermissions } from '../../common/permissions/redux/permissions.redux';
import { isTermedLevelSelectable } from '../../common/termedLevel/termedLevelSelectionValidationService';
import type { TermedLevel } from '../../common/termedLevel/types';
import { getRenewsToProductLabelFromContract } from '../../common/utils/productLabelUtil';
import { OverlayTrigger, Tooltip } from '../../common/utils/tooltipReExports';
import { DEFAULT_TOOLTIP_DELAY } from '../../config/config';
import { ContractTableTrackingAction, getContractTableAutoTrackingKey } from './contractTracking';

interface Props {
    contract: TermedLevel;
    onClick: (e: React.MouseEvent) => void;
    serviceName: string;
    hasPermission: boolean;
}

function RenewCell(props: Props) {
    const { contract, onClick, serviceName, hasPermission } = props;
    const { renewsToLevel } = contract;
    const intl = useIntl();

    const isRenewalEnabled = isTermedLevelSelectable(contract) && hasPermission;
    const renewalButton = (
        <Button
            className='btn btn-default btn-sm btn-icon-only'
            onClick={isRenewalEnabled ? onClick : undefined}
            disabled={!isRenewalEnabled}
            autoTrackingKey={getContractTableAutoTrackingKey(ContractTableTrackingAction.configureRenewal, serviceName)}
            type='button'
            data-onboarding-contracts-walkthrough-step='contract-view-renew-single-button'
        >
            <span className='uikit-icon icon-pencil' aria-hidden={true} />
        </Button>
    );

    const selectedOptionText =
        renewsToLevel === undefined ? (
            <>
                <span className='uikit-icon icon-remove text-color-danger margin-right-3' />
                <FormattedMessage id='marketplace.contracts.notRenewed' />
            </>
        ) : (
            <>
                <span className='uikit-icon icon-repeat text-color-success margin-right-3' />
                {getRenewsToProductLabelFromContract(contract, intl)}
            </>
        );

    return (
        <span className='display-flex align-items-center justify-content-between gap-10'>
            <span className='flex-1-1-0 line-height-125rel'>{selectedOptionText}</span>
            {hasPermission ? (
                isRenewalEnabled ? (
                    renewalButton
                ) : (
                    <OverlayTrigger
                        trigger={OverlayTrigger.TRIGGER_HOVER}
                        placement='top'
                        overlay={
                            <Tooltip id='contract-renewal-disabled-tooltip' className={'width-auto max-width-300'}>
                                <div style={{ whiteSpace: 'pre-line' }}>
                                    <FormattedMessage id='marketplace.contracts.renewal.disabled.tooltip' />
                                </div>
                            </Tooltip>
                        }
                        delay={DEFAULT_TOOLTIP_DELAY}
                    >
                        <span className='d-inline-block'>{renewalButton}</span>
                    </OverlayTrigger>
                )
            ) : null}
        </span>
    );
}

function makeMapStateToProps() {
    return (state: RootState) => ({
        hasPermission: getPermissions(state)?.includes(SUBSCRIPTION_WRITE) ?? false,
    });
}

export const ContractTableRowRenewCell = connect(makeMapStateToProps)(RenewCell);
